import { QueryClient, dehydrate, hydrate, type DehydratedState } from "@tanstack/react-query";
import { createRouter, Link, useRouter } from "@tanstack/react-router";
import { routeTree } from "./routeTree.gen";

function DefaultNotFound() {
  return (
    <div className="flex min-h-[70vh] items-center justify-center px-4">
      <div className="max-w-md text-center">
        <p className="text-xs uppercase tracking-[0.3em] text-gold">404 · The path is unmarked</p>
        <h1 className="mt-4 font-serif text-5xl text-gold-luminous">Return to the field.</h1>
        <p className="mt-4 text-sm text-muted-foreground">
          That transmission is not on this route. Return home or enter through the music.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/" className="rounded-full bg-gold-gradient px-5 py-2.5 text-sm font-semibold text-obsidian">
            Go home
          </Link>
          <Link
            to="/music"
            className="rounded-full border border-gold px-5 py-2.5 text-sm text-gold-luminous hover:bg-gold hover:text-obsidian"
          >
            Enter the music
          </Link>
        </div>
      </div>
    </div>
  );
}

function DefaultError({ error, reset }: { error: Error; reset: () => void }) {
  const router = useRouter();
  if (typeof console !== "undefined") console.error(error);
  return (
    <div className="flex min-h-[70vh] items-center justify-center px-4">
      <div className="max-w-md text-center">
        <p className="text-xs uppercase tracking-[0.3em] text-gold">Something interrupted the signal</p>
        <h1 className="mt-4 font-serif text-4xl text-gold-luminous">Try again in a breath.</h1>
        <p className="mt-4 text-sm text-muted-foreground">
          The page couldn't load. Retry, or return home.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="rounded-full bg-gold-gradient px-5 py-2.5 text-sm font-semibold text-obsidian"
          >
            Retry
          </button>
          <Link
            to="/"
            className="rounded-full border border-gold px-5 py-2.5 text-sm text-gold-luminous hover:bg-gold hover:text-obsidian"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

export const getRouter = () => {
  const queryClient = new QueryClient();

  const router = createRouter({
    routeTree,
    context: { queryClient },
    scrollRestoration: true,
    defaultPreloadStaleTime: 0,
    defaultErrorComponent: DefaultError,
    defaultNotFoundComponent: DefaultNotFound,
    // Carry the loader-populated query cache into the SSR payload so the
    // client's first render matches the server HTML instead of falling back
    // to a loading state (hydration mismatch).
    // The dehydrated cache is JSON-serializable at runtime; the router's
    // serializable constraint cannot prove that for query keys.
    dehydrate: () => ({ queryState: dehydrate(queryClient) }) as never,
    hydrate: (dehydrated: unknown) => {
      hydrate(queryClient, (dehydrated as { queryState: DehydratedState }).queryState);
    },
  });

  return router;
};
